/**
 * Helper function to extend a base class with one or more classes
 * @param derivedCtor Base class to extend
 * @param baseCtors Mixin classes to apply to base class
 */
function applyMixins(derivedCtor, baseCtors) {
    baseCtors.forEach((baseCtor) => {
        Object.getOwnPropertyNames(baseCtor.prototype).forEach((name) => {
            derivedCtor.prototype[name] = baseCtor.prototype[name];
        });
    });
}

/**
 * Method to filter array with an async callback function
 */
async function arrayFilterAsync(arr, callback) {
    const fail = Symbol();
    return (await Promise.all(arr.map(async (item) => ((await callback(item)) ? item : fail)))).filter((i) => i !== fail);
}

/**
 * Convert a value to a boolean
 */
function convertToBoolean(value) {
    if (!value || value === undefined || value === null) {
        return false;
    }
    if (typeof value === 'string') {
        value = value.toLowerCase();
    }
    switch (value) {
        case 'false':
        case 'no':
        case 'nej':
        case '0':
        case '':
            return false;
        default:
            return true;
    }
}

/**
 * Build reactive form group for attribute item
 *
 * Example: buildAttributeFormGroup(fb, 'email', {simple: email@example.com }, 'simple');
 *
 * @param {Formbuilder} fb
 * @param {String}      key
 * @param {Object}      attribute
 * @param {String}      property
 * @param {Boolean}     boolean
 *
 * @return FormGroup
 */
function buildAttributeFormGroup(fb, key, attr, property, boolean = false, options = false, disabled = false) {
    let value = attr ? attr[property] : null;
    if (property === 'json' && value) {
        try {
            value = JSON.parse(value);
        }
        catch (error) { }
    }
    if (boolean) {
        value = convertToBoolean(value);
    }
    const group = {
        alias: attr?.alias,
        uuid: attr?.uuid,
        resource_id: attr?.resource_id,
        key: attr?.key || key,
    };
    group[property] = [{ value, disabled }, options];
    return fb.group(group);
}

/**
 * Build reactive form group for meta item
 *
 * Example: buildMetaFormGroup(fb, 'email', {value: email@example.com });
 *
 * @param {Formbuilder} fb
 * @param {String}      key
 * @param {Object}      meta
 * @param {Boolean}     boolean
 *
 * @return FormGroup
 */
function buildMetaFormGroup(fb, key, meta, boolean = false, options = false, disabled = false) {
    let value = meta?.value;
    if (boolean) {
        value = convertToBoolean(meta?.value);
    }
    return fb.group({
        id: meta?.id,
        resource_id: meta?.resource_id,
        key: meta?.key || key,
        inherit: meta?.inherit,
        inherited_id: meta?.inherited_id,
        unique: meta?.unique,
        value: [{ value, disabled }, options],
        delete: meta?.delete || false,
    });
}

/**
 * Takes a class and creates a new instance of it with the same properties
 * @param instance Class to clone
 */
function cloneClass(instance) {
    const copy = new instance.constructor();
    Object.assign(copy, instance);
    return copy;
}

/**
 * Takes a number of arrays and removes duplicates and merges into one array
 *
 * @param p Property to use as key
 * @param arrs Infinite number of arrays to pass in
 */
const concatAndDeDuplicateObjects = (p, ...arrs) => {
    return [].concat(...arrs).reduce((a, b) => (!a.filter((c) => b[p] === c[p]).length ? [...a, b] : a), []);
};

/**
 * Helper method for getting an appropriate unit that I as a user actually has
 * a connection to. Solves the problem with push notifications either coming
 * from a unit in a different pipe than what I am currently standing in,
 * or coming from a unit higher up in the hierarchy.
 *
 * @param targetId uuid of target. Most often a unit id from a push notification
 * @param units Connected units from client with complete parent hierarchy
 * @returns Unit
 */
const getConnectedUnitFromUnitsByTarget = (targetId, units) => {
    const recurse = (target, unit) => {
        if (unit.uuid === target) {
            return unit;
        }
        if (unit.parent) {
            return recurse(target, unit.parent);
        }
    };
    for (const unit of units) {
        const found = recurse(targetId, unit);
        if (found) {
            return unit;
        }
    }
    return null;
};

/**
 * Takes an input string and creates a HEX hash from it
 * @param str Input string, for example a uuid
 * @returns HEX code
 */
function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
}

/**
 * Takes number of bytes and converts it to a human readable string
 * @param bytes Number of bytes
 * @param si Threshhold
 */
function humanFileSize(bytes, si = false) {
    const thresh = si ? 1000 : 1024;
    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }
    const units = si ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    do {
        bytes /= thresh;
        ++u;
    } while (Math.abs(bytes) >= thresh && u < units.length - 1);
    return bytes.toFixed(1) + ' ' + units[u];
}

/**
 * Check to see if value is valid json
 * @param {any} item
 */
function isJson(item) {
    item = typeof item !== 'string' ? JSON.stringify(item) : item;
    try {
        item = JSON.parse(item);
    }
    catch (e) {
        return false;
    }
    if (typeof item === 'object' && item !== null) {
        return true;
    }
    return false;
}

/**
 * Check to see if value is object
 * @param item
 */
function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Check to see if string is a valid UUID
 * @param {string} uuid
 */
function isValidUUID(uuid) {
    return /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.test(uuid);
}

/**
 * Do a deep object merge
 * @param target Target object
 * @param sources Merge sources
 */
function mergeDeep(target, ...sources) {
    if (!sources.length)
        return target;
    const source = sources.shift();
    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key])
                    Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            }
            else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }
    return mergeDeep(target, ...sources);
}

/**
 * Create URL params from object
 * @param data Object
 */
function paramify(data) {
    return Object.entries(data)
        .map(([key, val]) => `${key}=${val}`)
        .join('&');
}

/**
 * Generates a random string
 * @param length Optional length parameter, otherwise its the length of all the available chars. (62)
 */
function randomString(length) {
    let result = '';
    const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let i = length ? length : chars.length; i > 0; --i) {
        result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
}

/**
 * Reorders array
 * @param event new and old index
 * @param originalArray Array to reorder
 * @param updatePosition Flag to update position property
 */
function reorderArray(event, originalArray, updatePosition = false) {
    const movedItem = originalArray.find((_, index) => index === event.oldIndex);
    const remainingItems = originalArray.filter((_, index) => index !== event.oldIndex);
    let reorderedItems = [...remainingItems.slice(0, event.newIndex), movedItem, ...remainingItems.slice(event.newIndex)];
    if (updatePosition) {
        reorderedItems = reorderedItems.map((item, index) => {
            item.position = index;
            return item;
        });
    }
    return reorderedItems;
}

/**
 * Function to serialize an aware model and its children,
 * for storing in local storage
 *
 * @param model AwareModel instance
 */
function serializeAwareModel(model) {
    const obj = {};
    const fields = model.model.fields;
    for (const field of fields) {
        let value = model[field];
        if (Array.isArray(value) && value[0]?.model) {
            value = value.map((v) => serializeAwareModel(v));
        }
        else if (value?.model) {
            value = serializeAwareModel(value);
        }
        obj[field] = value;
    }
    return obj;
}

/**
 * Takes a string and slugifies it
 * @param str String to slugify
 */
function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, ''); // trim
    str = str.toLowerCase();
    // remove accents, swap ñ for n, etc
    const from = 'ãàáäåâẽèéëêìíïîõòóöôùúüûñç·/_,:;';
    const to = 'aaaaaeeeeeiiiiooooouuuunc------';
    for (let i = 0, l = from.length; i < l; i++) {
        str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }
    str = str
        .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-'); // collapse dashes
    return str;
}

/**
 * Generate a color code based on a string input.
 * Color is always the same given the same input.
 *
 * @param str Random input string, for example a uuid
 * @returns HEX color code
 */
function stringToColor(str) {
    let hash = hashString(str);
    let colour = '#';
    for (var i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        colour += ('00' + value.toString(16)).substr(-2);
    }
    return colour;
}

/**
 * Takes a url to a file (local or on the web)
 * and downloads it and creates a file of it.
 * @param url Path to file
 * @param filename Name of the new file
 * @param mimeType Mime type, ex: image/jpeg
 */
function urlToFile(url, filename = 'image.jpg', mimeType) {
    return fetch(url)
        .then(function (res) {
        return res.arrayBuffer();
    })
        .then(function (buf) {
        return new File([buf], filename, { type: mimeType });
    });
}

function mix(client, mixins) {
    const clientKeys = Object.getOwnPropertyNames(client.prototype);
    for (let mixin of mixins) {
        const mixinMixables = getMixables(clientKeys, mixin);
        Object.defineProperties(client.prototype, mixinMixables);
    }
}
/**
 * Returns a map of mixables. That is things that can be mixed in
 */
function getMixables(clientKeys, mixin) {
    let descriptors = {};
    switch (typeof mixin) {
        case 'object':
            descriptors = getMixables(mixin);
            break;
        case 'function':
            descriptors = getMixables(mixin.prototype);
            break;
    }
    return descriptors;
    function getMixables(obj) {
        const map = {};
        Object.getOwnPropertyNames(obj).map((key) => {
            if (clientKeys.indexOf(key) < 0) {
                const descriptor = Object.getOwnPropertyDescriptor(obj, key);
                if (descriptor === undefined)
                    return;
                if (descriptor.get || descriptor.set) {
                    map[key] = descriptor;
                }
                else if (typeof descriptor.value === 'function') {
                    map[key] = descriptor;
                }
            }
        });
        return map;
    }
}
/**
 * Takes a list of classes or object literals and adds their methods
 * to the class calling it.
 */
function Use(...options) {
    return function (target, _) {
        mix(target.constructor, options.reverse());
    };
}
/**
 * Takes a method as a parameter and add it to the class calling it.
 */
function Delegate(method) {
    return function (target, propertyKey) {
        target.constructor.prototype[propertyKey] = method;
    };
}

/*
 * Public API Surface of helpers
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Delegate, Use, applyMixins, arrayFilterAsync, buildAttributeFormGroup, buildMetaFormGroup, cloneClass, concatAndDeDuplicateObjects, convertToBoolean, getConnectedUnitFromUnitsByTarget, hashString, humanFileSize, isJson, isObject, isValidUUID, mergeDeep, paramify, randomString, reorderArray, serializeAwareModel, slugify, stringToColor, urlToFile };
